import { Command } from 'commander';
import {
	getProjectConfig,
	PROJECT_CONFIG_NAME,
	ProjectConfig,
	projectConfigSchema,
	resolvePaths
} from '../utils/config';
import * as v from 'valibot';
import { McpServer } from '@modelcontextprotocol/sdk/server/mcp.js';
import { StdioServerTransport } from '@modelcontextprotocol/sdk/server/stdio.js';
import { CallToolResult, TextContent } from '@modelcontextprotocol/sdk/types.js';
import pkg from '../../package.json';
import { DEFAULT_REPOS, NGM_DEV_CLI_NAME } from '../constants';
import * as registry from '../utils/registry-providers/internal';
import { z } from 'zod';
import { getWatermark } from '../utils/get-watermark';
import { getBlockFilePath, preloadBlocks } from '../utils/blocks';
import { loadFormatterConfig } from '../utils/format';
import { transformRemoteContent } from '../utils/files';
import fs from 'fs/promises';
import path from 'path';
import os from 'os';
// Normalize all paths consistently
function normalizePath(p: string): string {
	return path.normalize(p);
}
function expandHome(filepath: string): string {
	if (filepath.startsWith('~/') || filepath === '~') {
		return path.join(os.homedir(), filepath.slice(1));
	}
	return filepath;
}
const schema = v.object({
	verbose: v.boolean(),
	workingDir: v.optional(v.string()),
	config: v.optional(projectConfigSchema)
});

type Options = v.InferInput<typeof schema>;

export const mcp = new Command('mcp')
	.description('Run an MCP server (Not stable yet).')
	.argument(
		'[working-dir]',
		'The directory to root of the project, where blocks need to be generated'
	)
	.option('-v, --verbose', 'Verbose output', false)
	.action(async (workingDir, opts) => {
		const options = v.parse(schema, opts);
		await _mcp(options, workingDir);
	});

async function _mcp(options: Options, workingDir?: string) {
	let errorMessage = '';

	const resolvedRepos: registry.RegistryProviderState[] = (
		await registry.forEachPathGetProviderState(DEFAULT_REPOS, { noCache: true })
	).match(
		(val) => val,
		({ repo }) => {
			errorMessage = `Failed to get info for ${repo}`;
			return [];
		}
	);

	if (errorMessage) {
		return {
			content: [{ type: 'text' as const, text: errorMessage }]
		};
	}

	// verbose(`Resolved ${color.cyan(repoPaths.join(', '))}`);

	// verbose(`Fetching blocks from ${color.cyan(repoPaths.join(', '))}`);

	const manifests = (await registry.fetchManifests(...resolvedRepos)).match(
		(v) => v,
		({ repo }) => {
			errorMessage = `Failed fetching blocks from ${repo}`;
			return [];
		}
	);

	if (errorMessage) {
		return {
			content: [{ type: 'text' as const, text: errorMessage }]
		};
	}

	const blocksMap = registry.getRemoteBlocks(manifests);

	// if (!options.verbose && !options.silent)
	// 	loading.stop(`Retrieved blocks from ${color.cyan(repoPaths.join(', '))}`);

	// verbose(`Retrieved blocks from ${color.cyan(repoPaths.join(', '))}`);
	// Create server instance
	const server = new McpServer({
		name: 'angular-block-generating-service',
		version: pkg.version
	});

	server.tool(
		'generate-angular-block',
		'Generate an angular block using @angular/material and Tailwind CSS',
		{
			blockNames: z
				.array(z.string())
				.nonempty()
				.describe(
					`Names of the blocks to be added in the project, e.g.: "free-authentication/login-with-email-password".
You can try to understand the block name from user's prompt. Or you can ask the user to provide the block name.
But, it must be provided. All the possible block names are: ${Array.from(blocksMap.entries())
						// eslint-disable-next-line @typescript-eslint/no-unused-vars
						.filter(([_, value]) => value.list)
						// eslint-disable-next-line @typescript-eslint/no-unused-vars
						.map(([_, value]) => `"${value.category}/${value.name}"`)
						.join(', ')}`
				),
			...(!workingDir
				? {
						projectDir: z
							.string()
							.nonempty()
							.describe(
								`The directory to generate the Angular Material Blocks. Under no circumstances, this can be empty. Ideally, this should be the root of the project, where ${PROJECT_CONFIG_NAME} file is present. If not, please ask user to provide the correct directory.`
							)
					}
				: {})
		},
		async ({ blockNames, projectDir }) => {
			if (errorMessage) {
				return {
					content: [{ type: 'text' as const, text: errorMessage }]
				};
			}

			try {
				const finalWorkingDir = workingDir ?? (projectDir as string);
				// Store allowed directories in normalized form
				const allowedDirectory = normalizePath(path.resolve(expandHome(finalWorkingDir)));

				// Validate that all directories exist and are accessible
				try {
					const stats = await fs.stat(expandHome(allowedDirectory));
					if (!stats.isDirectory()) {
						console.error(`Error: ${allowedDirectory} is not a directory`);
						process.exit(1);
					}
				} catch (error) {
					console.error(`Error accessing directory ${allowedDirectory}:`, error);
					process.exit(1);
				}

				// Security utilities
				async function validatePath(requestedPath: string, askToCreate?: boolean): Promise<string> {
					const expandedPath = expandHome(requestedPath);
					const absolute = path.isAbsolute(expandedPath)
						? path.resolve(expandedPath)
						: path.resolve(process.cwd(), expandedPath);

					const normalizedRequested = normalizePath(absolute);

					// Check if path is within allowed directories
					const isAllowed = normalizedRequested.startsWith(allowedDirectory);
					if (!isAllowed) {
						throw new Error(
							`Access denied - path outside allowed directories: ${absolute} not in ${allowedDirectory}`
						);
					}

					// Handle symlinks by checking their real path
					try {
						const realPath = await fs.realpath(absolute);
						const normalizedReal = normalizePath(realPath);
						const isRealPathAllowed = normalizedReal.startsWith(allowedDirectory);
						if (!isRealPathAllowed) {
							throw new Error('Access denied - symlink target outside allowed directories');
						}
						return realPath;
					} catch {
						// For new files that don't exist yet, verify parent directory
						const parentDir = path.dirname(absolute);
						try {
							const realParentPath = await fs.realpath(parentDir);
							const normalizedParent = normalizePath(realParentPath);
							const isParentAllowed = normalizedParent.startsWith(allowedDirectory);
							if (!isParentAllowed) {
								throw new Error('Access denied - parent directory outside allowed directories');
							}
							return absolute;
						} catch {
							throw new Error(
								`Parent directory does not exist: ${parentDir}.` +
									(askToCreate ? ` Please ask user to create the directory: ${parentDir}` : '')
							);
						}
					}
				}
				const configResult = getProjectConfig(finalWorkingDir);

				let config: ProjectConfig;

				if (configResult.isErr()) {
					// ask user to run npx @ngm-dev/cli init command
					throw new Error(
						`${PROJECT_CONFIG_NAME} not found at ${finalWorkingDir}. Please ask user to run \`npx ${NGM_DEV_CLI_NAME} init\` command to create a config file`
					);
				} else {
					config = configResult.unwrap();
				}
				const repoPaths = config.repos;

				if (repoPaths.length === 0) {
					return {
						content: [{ type: 'text' as const, text: 'No repos found' }]
					};
				}

				// verbose(`Resolving ${color.cyan(repoPaths.join(', '))}`);

				// if (!options.verbose && !options.silent)
				// 	loading.start(`Fetching blocks from ${color.cyan(repoPaths.join(', '))}`);
				// Find blocks that match the requested names
				const installingBlocks: registry.RemoteBlock[] = [];

				for (const blockName of blockNames) {
					// Find block in the blocksMap
					const matchingBlockEntries = Array.from(blocksMap.entries()).filter(([key]) =>
						key.endsWith(`/${blockName}`)
					);

					if (matchingBlockEntries.length === 0) {
						return {
							content: [{ type: 'text' as const, text: `Block "${blockName}" not found` }]
						};
					}

					installingBlocks.push(matchingBlockEntries[0][1]);
				}

				if (installingBlocks.length === 0) {
					return {
						content: [{ type: 'text' as const, text: 'No blocks found for the given names' }]
					};
				}

				// Get resolved paths for file creation
				const resolvedPathsResult = resolvePaths(config.paths, finalWorkingDir);
				if (resolvedPathsResult.isErr()) {
					return {
						content: [{ type: 'text' as const, text: resolvedPathsResult.unwrapErr() }]
					};
				}

				const resolvedPaths = resolvedPathsResult.unwrap();

				// Load formatter config for processing files
				const { prettierOptions } = await loadFormatterConfig({
					cwd: finalWorkingDir
				});

				// Preload blocks to get their files
				const preloadedBlocks = preloadBlocks(installingBlocks, config, options.verbose);

				// Create an array to store content entries
				const content: CallToolResult = {
					content: []
				};
				const textContentList: TextContent[] = [];
				// const fileContentList: ResourceContents[] = [];

				// Process each block
				for (const preloadedBlock of preloadedBlocks) {
					const shortSpecifier = `${preloadedBlock.block.category}/${preloadedBlock.block.name}`;

					// Get watermark for the files
					const watermark = getWatermark(preloadedBlock.block.sourceRepo.url, shortSpecifier);

					// Get files for the block
					const files = await preloadedBlock.files;

					// Process each file
					for (const file of files) {
						const contentResult = file.content;
						if (contentResult.isErr()) {
							const errorMsg = `Error getting content for ${file.name}: ${contentResult.unwrapErr()}`;
							textContentList.push({
								type: 'text' as const,
								text: `**Error**: ${errorMsg}`
							});
							continue;
						}

						const content = contentResult.unwrap();

						// Get destination path for the file
						const destPath = getBlockFilePath(
							file.name,
							preloadedBlock.block,
							resolvedPaths,
							finalWorkingDir
						);

						// Transform content to match user's project needs
						const remoteContentResult = await transformRemoteContent({
							file: {
								content,
								destPath
							},
							prettierOptions,
							config,
							imports: preloadedBlock.block._imports_ || {},
							watermark,
							verbose: options.verbose ? console.error : undefined,
							cwd: finalWorkingDir
						});

						if (remoteContentResult.isErr()) {
							const errorMsg = `Error transforming content for ${file.name}: ${remoteContentResult.unwrapErr()}`;
							textContentList.push({
								type: 'text' as const,
								text: `**Error**: ${errorMsg}`
							});
							continue;
						}

						const remoteContent = remoteContentResult.unwrap();

						const validPath = await validatePath(destPath, true);

						await fs.writeFile(validPath, remoteContent, { encoding: 'utf-8' });

						textContentList.push({
							type: 'text' as const,
							text: `Successfully wrote to ${destPath}`
						});
					}
				}

				content.content = textContentList;
				// content.contents = fileContentList;
				// Return all content entries
				return content;
			} catch (error: unknown) {
				const errorMsg = error instanceof Error ? error.message : String(error);
				return {
					content: [{ type: 'text' as const, text: `Error preparing blocks: ${errorMsg}` }]
				};
			}
		}
	);

	async function main() {
		const transport = new StdioServerTransport();
		await server.connect(transport);
		console.error('Angular block generating service MCP Server running on stdio');
	}

	main().catch((error) => {
		console.error('Fatal error in main():', error);
		process.exit(1);
	});
}
